import BUILD_VERSION from '../../buildVersion';

import {
    ensurePageView,
    getAnalyticsIdentityContext,
    nextTabSequence
} from './identityManager';

import {
    getAnalyticsActorContext
} from './actorContextManager';

import {
    getAnalyticsAcquisitionContext
} from './acquisitionManager';

import {
    enqueueAnalyticsEvent
} from './eventQueue';

import {
    flushAnalyticsQueue
} from './analyticsSender';

import {
    resolveAnalyticsPageType
} from './pageContext';

const TRACKING_VERSION =
    '2.0.0';

const EVENT_VERSION =
    1;

const FORBIDDEN_PII_KEYS =
    new Set([
        'phone',
        'phonenumber',
        'telephone',
        'name',
        'firstname',
        'lastname',
        'email',
        'comment',
        'message',
        'messagetext',
        'text',
        'password',
        'token',
        'accesstoken',
        'refreshtoken'
    ]);

export const ANALYTICS_V2_EVENTS =
    Object.freeze({
        PAGE_VIEW:
            'page_view',

        CATALOG_VIEW:
            'catalog_view',

        CATALOG_DEPTH_REACHED:
            'catalog_depth_reached',

        CATALOG_MAP_OPEN:
            'catalog_map_open',

        LISTING_IMPRESSION:
            'listing_impression',

        LISTING_CLICK:
            'listing_click',

        ADD_VIEW:
            'add_view',

        LISTING_BACK_TO_CATALOG:
            'listing_back_to_catalog',

        SEO_COLLECTION_CLICK:
            'seo_collection_click',

        NEARBY_PLACE_IMPRESSION:
            'nearby_place_impression',

        PLACE_IMPRESSION:
            'place_impression',

        PLACE_CLICK:
            'place_click',

        GALLERY_OPEN:
            'gallery_open',

        GALLERY_DEPTH_REACHED:
            'gallery_depth_reached',

        FAVOURITE_ADD:
            'favourite_add',

        FAVOURITE_REMOVE:
            'favourite_remove',

        SHOW_PHONE:
            'show_phone',

        COPY_PHONE:
            'copy_phone',

        PHONE_CALL_CLICK:
            'phone_call_click',

        PHONE_MANAGER_CLICK:
            'phone_manager_click',

        TELEGRAM_CHANNEL_CLICK:
            'telegram_channel_click',

        FILTER_OPEN:
            'filter_open',

        FILTER_APPLY:
            'filter_apply',

        FILTER_ZERO_RESULTS:
            'filter_zero_results',

        SELECTION_FORM_OPEN:
            'selection_form_open',

        SELECTION_FORM_STARTED:
            'selection_form_started',

        SELECTION_STEP_REACHED:
            'selection_step_reached',

        SELECTION_VALIDATION_ERROR:
            'selection_validation_error',

        SELECTION_FORM_CLOSE:
            'selection_form_close',

        REQUEST_SENT:
            'request_sent',

        CHAT_OPEN:
            'chat_open'
    });

function createUuid() {
    const cryptoApi =
        typeof window !== 'undefined'
            ? window.crypto
            : null;

    if (
        cryptoApi?.randomUUID
    ) {
        return cryptoApi
            .randomUUID();
    }

    if (
        cryptoApi?.getRandomValues
    ) {
        const bytes =
            new Uint8Array(16);

        cryptoApi
            .getRandomValues(
                bytes
            );

        bytes[6] =
            (bytes[6] & 0x0f) |
            0x40;

        bytes[8] =
            (bytes[8] & 0x3f) |
            0x80;

        return Array
            .from(
                bytes,
                byte =>
                    byte
                        .toString(16)
                        .padStart(
                            2,
                            '0'
                        )
            )
            .join('')
            .replace(
                /^(.{8})(.{4})(.{4})(.{4})(.{12})$/,
                '$1-$2-$3-$4-$5'
            );
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
        .replace(
            /[xy]/g,
            char => {
                const random =
                    Math.floor(
                        Math.random() *
                        16
                    );

                const value =
                    char === 'x'
                        ? random
                        : (
                            random &
                            0x3
                        ) |
                        0x8;

                return value
                    .toString(16);
            }
        );
}

function isPlainObject(value) {
    if (
        !value ||
        typeof value !==
        'object' ||
        Array.isArray(value)
    ) {
        return false;
    }

    const prototype =
        Object.getPrototypeOf(
            value
        );

    return (
        prototype ===
        Object.prototype ||
        prototype ===
        null
    );
}

function findForbiddenPiiKey(
    value,
    depth = 0
) {
    if (
        depth > 8 ||
        value === null ||
        value === undefined
    ) {
        return null;
    }

    if (
        Array.isArray(value)
    ) {
        for (
            const item
            of value
            ) {
            const found =
                findForbiddenPiiKey(
                    item,
                    depth + 1
                );

            if (found) {
                return found;
            }
        }

        return null;
    }

    if (
        !isPlainObject(value)
    ) {
        return null;
    }

    for (
        const [
            key,
            child
        ]
        of Object.entries(
        value
    )
        ) {
        if (
            FORBIDDEN_PII_KEYS
                .has(
                    String(key)
                        .toLowerCase()
                )
        ) {
            return key;
        }

        const found =
            findForbiddenPiiKey(
                child,
                depth + 1
            );

        if (found) {
            return found;
        }
    }

    return null;
}

function addOptional(
    target,
    key,
    value
) {
    if (
        value !== undefined &&
        value !== null &&
        value !== ''
    ) {
        target[key] =
            value;
    }
}

function getPathname() {
    return (
        typeof window !==
        'undefined'
            ? window.location
                .pathname ||
            '/'
            : '/'
    );
}

export async function trackAnalyticsV2(
    event,
    options = {}
) {
    try {
        if (
            !event ||
            typeof event !==
            'string'
        ) {
            throw new Error(
                'Analytics V2: event name is required'
            );
        }

        /*
         * Время capture,
         * а не время HTTP request.
         */
        const occurredAtMs =
            Date.now();

        const occurredAt =
            new Date(
                occurredAtMs
            ).toISOString();

        const pathname =
            getPathname();

        const pageView =
            ensurePageView(
                pathname
            );

        const identity =
            await getAnalyticsIdentityContext(
                occurredAtMs
            );

        const tabSequence =
            await nextTabSequence();

        const actorContext =
            await getAnalyticsActorContext(
                identity.visitorId
            );

        const acquisition =
            getAnalyticsAcquisitionContext(
                identity.visitId
            );

        const payload =
            options.payload ===
            undefined
                ? {}
                : options.payload;

        if (
            !isPlainObject(
                payload
            )
        ) {
            throw new Error(
                'Analytics V2: payload must be an object'
            );
        }

        const forbiddenPiiKey =
            findForbiddenPiiKey(
                payload
            );

        if (
            forbiddenPiiKey
        ) {
            throw new Error(
                `Analytics V2: forbidden PII key in payload: ${forbiddenPiiKey}`
            );
        }

        const rawEvent = {
            eventId:
                createUuid(),

            event,

            eventVersion:
            EVENT_VERSION,

            trackingVersion:
            TRACKING_VERSION,

            identityVersion:
            identity
                .identityVersion,

            appVersion:
            BUILD_VERSION,

            occurredAt,

            visitorId:
            identity.visitorId,

            visitId:
            identity.visitId,

            tabId:
            identity.tabId,

            tabSequence,

            pageViewId:
            pageView.pageViewId,

            pageType:
                options.pageType ||
                resolveAnalyticsPageType(
                    pathname
                ),

            pagePath:
            pageView.pagePath,

            actorContextToken:
            actorContext
                .actorContextToken,

            payload
        };

        /*
         * Только разрешённые
         * top-level client fields.
         *
         * ownerId/userId/actorType
         * frontend сюда физически
         * не прокидывает.
         */
        for (
            const field
            of [
            'surface',

            'addId',
            'position',

            'searchContextId',
            'filtersHash',
            'sort',

            'rotationId',
            'rankingReason',
            'rankingTier',

            'chatId',

            'bookingAttemptId',
            'bookingId',

            'requestId',
            'parentEventId'
        ]
            ) {
            addOptional(
                rawEvent,
                field,
                options[field]
            );
        }

        /*
         * Acquisition привязан
         * к visitId и автоматически
         * прикладывается к событиям.
         */
        for (
            const field
            of [
            'landingPath',
            'landingReferrer',

            'utmSource',
            'utmMedium',
            'utmCampaign',
            'utmContent',
            'utmTerm',

            'yclid',
            'gclid'
        ]
            ) {
            addOptional(
                rawEvent,
                field,
                acquisition[field]
            );
        }

        /*
         * СНАЧАЛА durable queue.
         */
        await enqueueAnalyticsEvent(
            rawEvent
        );

        /*
         * ПОТОМ сеть.
         *
         * Не ждём HTTP ACK
         * в пользовательском action.
         */
        flushAnalyticsQueue();

        return rawEvent.eventId;

    } catch (error) {
        /*
         * Analytics никогда не должна
         * ломать основной интерфейс.
         */
        console.warn(
            `[AnalyticsV2] Capture failed for ${String(event)}:`,
            error?.message ||
            error
        );

        return null;
    }
}

export const analyticsV2 =
    Object.freeze({
        track:
        trackAnalyticsV2,

        events:
        ANALYTICS_V2_EVENTS
    });